/**
 * CheckQuestion component
 *
 * Description:
 * Renders a multiple-choice question with checkboxes, allowing users to select one or more answers.
 * Handles answer selection and dispatches updates to the Redux store.
 *
 * Features:
 * - Displays all choices for a question as checkboxes.
 * - Handles user selection and updates answers in Redux.
 * - Disables checkboxes if the answer is already set.
 * - Uses Material-UI for UI elements.
 */
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
// import { useTranslation } from 'react-i18next';

import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import { getQuiz, saveUpdateAnswer } from 'store/lesson-actions';

import classes from './CheckQuestion.module.css';

const CheckQuestion = props => {
  // const { t } = useTranslation(['lesson']);
  const dispatch = useDispatch();
  const dataAcademy = useSelector(state => state.academyInfo);
  const dataLesson = useSelector(state => state.lessonInfo);

  useEffect(() => {
    props.questionData.choices.map(choice => {
      if (choice.answer) {
        props.nextQuestion();
      }
    });
  }, []);

  /**
   * Handles checkbox change event for each answer.
   * Updates Redux and triggers next question.
   */
  const handleChange = (idChoice, status) => () => {
    props.nextQuestion();
    (async () => {
      const answer = await saveUpdateAnswer(
        props.idApplication,
        idChoice,
        status
      );
      if (answer) {
        dispatch(
          getQuiz(
            dataLesson.data.idQuiz,
            dataLesson.quizEvaluableData.idApplication,
            dataAcademy.data.shortName
          )
        );
      }
    })();
  };

  return (
    <>
      {/* Custom prop: 'className' for question styling */}
      <p className={classes.question}>{props.questionData.body}</p>
      {/* <p className={classes.text}>{t('enter_an_option')}</p> */}
      <FormGroup>
        {props.questionData.choices.map(choice => (
          <FormControlLabel
            key={choice.idChoice}
            // Custom prop: 'control' passes Checkbox with checked, onChange, and disabled logic
            control={
              <Checkbox
                checked={choice.answer}
                onChange={handleChange(
                  choice.idChoice,
                  choice.answer ? false : true
                )}
                sx={{
                  color: '#a41034',
                  '&.Mui-checked': {
                    color: '#eeeeee'
                  }
                }}
              />
            }
            label={choice.body}
            // Custom prop: 'className' for answer styling
            className={classes.answer}
          />
        ))}
      </FormGroup>
    </>
  );
};

CheckQuestion.propTypes = {
  /**
   * Question data object containing choices and question info
   */
  questionData: PropTypes.object,
  /**
   * Application ID for answer submission
   */
  idApplication: PropTypes.number,
  /**
   * Current question index
   */
  currentQuestion: PropTypes.number,
  /**
   * Function to go to the next question
   */
  nextQuestion: PropTypes.func
};

CheckQuestion.defaultProps = {
  questionData: {},
  idApplication: undefined,
  currentQuestion: undefined,
  nextQuestion: undefined
};

export default CheckQuestion;
